/**
 * VOCABULÁRIO DO ANÚNCIO DO MERCADO LIVRE — código da API → o nome que o
 * vendedor vê no próprio Mercado Livre.
 *
 * A API só devolve o código (`gold_special`, `me2`), e as telas vinham
 * imprimindo o código cru: "Tipo gold_special · Envio me2" não diz nada a quem
 * opera. Estes mapas não existiam em lugar nenhum do sistema — o único parente
 * é `mlClaimCatalog.js`, que traduz o vocabulário de RECLAMAÇÕES; o de anúncio
 * estava faltando, e cada tela que precisou dele até hoje ou imprimiu o código
 * ou comparou string solta (`listing_type_id === 'gold_pro'`).
 *
 * Fonte dos nomes: `GET /sites/MLB/listing_types` (tipo de anúncio) e o campo
 * `shipping.mode` da doc de items. São vocabulários FECHADOS e estáveis — por
 * isso um mapa aqui em vez de mais uma tabela espelho para sincronizar.
 *
 * ⚠️ Código desconhecido volta EXATAMENTE como veio. Nunca inventar nome: se o
 * ML criar um tipo novo, a tela mostra o código (feio, porém verdadeiro) e não
 * um rótulo errado.
 */

/** Tipo de anúncio (`listing_type_id`). Os legados ainda aparecem em conta antiga. */
export const LISTING_TYPE_LABELS = {
    gold_pro: 'Premium',
    gold_special: 'Clássico',
    gold: 'Ouro',
    gold_premium: 'Ouro Premium',
    silver: 'Prata',
    bronze: 'Bronze',
    free: 'Grátis',
};

/** O que cada tipo muda na prática — vira `title` do chip. */
export const LISTING_TYPE_HINTS = {
    gold_pro: 'Premium: parcelamento sem juros por conta do vendedor, tarifa mais alta.',
    gold_special: 'Clássico: exposição alta, sem parcelamento sem juros por conta do vendedor.',
    free: 'Grátis: exposição baixa e sem tarifa por venda.',
};

/** Modalidade de envio (`shipping.mode`). */
export const SHIPPING_MODE_LABELS = {
    me2: 'Mercado Envios',
    me1: 'Mercado Envios 1',
    custom: 'Frete combinado',
    not_specified: 'Envio não especificado',
};

export const SHIPPING_MODE_HINTS = {
    me2: 'Mercado Envios: etiqueta e frete pelo ML (Full, Flex, coleta ou agência).',
    me1: 'Mercado Envios 1: modalidade antiga do ML.',
    custom: 'Frete combinado direto com o comprador — fora do Mercado Envios.',
};

/**
 * Tipo de logística (`logistic_type`) — de onde a mercadoria sai. Fica aqui
 * junto porque é o mesmo assunto e a mesma origem, ainda que a linha do monitor
 * não traga a coluna hoje.
 */
export const LOGISTIC_TYPE_LABELS = {
    fulfillment: 'Full',
    self_service: 'Flex',
    drop_off: 'Agência',
    cross_docking: 'Coleta',
    xd_drop_off: 'Coleta ou agência',
    not_specified: 'Não especificado',
};

/** Código → `{ code, label, hint }`. Vazio/nulo devolve `null` (a tela omite o chip). */
function Describe(labels, hints, code) {
    const normalized = String(code ?? '').trim();
    if (!normalized) {
        return null;
    }
    return {
        code: normalized,
        label: labels[normalized] || normalized,
        hint: hints?.[normalized] || null,
    };
}

export const DescribeListingType = (code) => Describe(LISTING_TYPE_LABELS, LISTING_TYPE_HINTS, code);
export const DescribeShippingMode = (code) => Describe(SHIPPING_MODE_LABELS, SHIPPING_MODE_HINTS, code);
export const DescribeLogisticType = (code) => Describe(LOGISTIC_TYPE_LABELS, null, code);
